import React, { useState } from "react";
import { X, Send, Award, Star, FileText } from "lucide-react";
import { addActivityComment } from "../../../../services/professorActivityService";

const GradeActivityModal = ({ activity, onClose, onSubmitted, professorProfile, initialDecision, fromSource = "Professor" }) => {
  const targetSource = fromSource || "Professor";
  const isHodContext = targetSource === "HOD";

  const existingComment = isHodContext
    ? activity?.comments?.find((c) => c.from_source === "HOD")
    : activity?.comments?.find((c) => c.from_source !== "HOD") || activity?.comments?.find((c) => c.professorEmail === professorProfile?.email);

  const existingReaction = isHodContext
    ? activity?.reactions?.find((r) => r.from_source === "HOD")
    : activity?.reactions?.find((r) => r.from_source !== "HOD") || activity?.reactions?.find((r) => r.professorEmail === professorProfile?.email);

  const [commentText, setCommentText] = useState(existingComment?.text || "");
  const [rating, setRating] = useState(existingComment?.assessment?.rating || existingReaction?.rating || "Satisfactory");
  const [numericScore, setNumericScore] = useState(
    existingComment?.assessment?.numericScore !== undefined && existingComment?.assessment?.numericScore !== null
      ? existingComment.assessment.numericScore
      : existingReaction?.score || ""
  );
  const [decision, setDecision] = useState(
    initialDecision ||
    existingComment?.assessment?.facultyDecision ||
    existingReaction?.decision ||
    (activity?.status === "REJECTED" ? "Rejected" : "Approved")
  );
  const [includeSignature, setIncludeSignature] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!activity) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim() && !numericScore && !rating) {
      setError("Please provide a comment or rating for this activity review.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const res = await addActivityComment(activity.id, {
        text: commentText.trim(),
        rating,
        numericScore: numericScore ? parseFloat(numericScore) : null,
        facultyDecision: decision,
        status: decision === "Approved" ? "APPROVED" : "REJECTED",
        signature: includeSignature ? professorProfile?.signature : null,
        from_source: targetSource,
      });

      if (onSubmitted) onSubmitted(res);
      onClose();
    } catch (err) {
      console.error("Error submitting grade:", err);
      const msg = err.response?.data?.error || err.message || "Failed to submit evaluation";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isHodContext ? "bg-purple-50 border-purple-100" : "bg-slate-50/80 border-slate-100"}`}>
          <div className="flex items-center gap-2">
            <Award className={`w-5 h-5 ${isHodContext ? "text-purple-600" : "text-indigo-600"}`} />
            <h3 className={`font-bold text-base ${isHodContext ? "text-purple-900" : "text-slate-900"}`}>
              {isHodContext ? "🏛️ HOD Grade & Assess Activity" : "Grade & Assess Activity"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
          {error && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs">
              {error}
            </div>
          )}

          {/* Activity Title Banner */}
          <div className="p-3 rounded-xl bg-slate-100/70 border border-slate-200">
            <p className="font-semibold text-slate-500 uppercase text-[10px] tracking-wider mb-0.5">
              Activity Title
            </p>
            <p className="font-bold text-slate-900 text-xs">{activity.title}</p>
          </div>

          {/* Decision */}
          <div>
            <label className="block font-bold text-slate-700 mb-1">
              {isHodContext ? "HOD Decision" : "Faculty Decision"}
            </label>
            <select
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              className="w-full bg-white border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              <option value="Approved">Approved ✅</option>
              <option value="Rejected">Rejected ❌</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Rating */}
            <div>
              <label className="block font-bold text-slate-700 mb-1 flex items-center gap-1">
                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" /> Rating
              </label>
              <select
                value={rating}
                onChange={(e) => setRating(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg p-2 font-medium text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              >
                <option value="Excellent">Excellent</option>
                <option value="Good">Good</option>
                <option value="Satisfactory">Satisfactory</option>
                <option value="Unsatisfactory">Unsatisfactory</option>
              </select>
            </div>

            {/* Numeric Score */}
            <div>
              <label className="block font-bold text-slate-700 mb-1">
                Numeric Score (out of 10)
              </label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.5"
                placeholder="e.g. 8.5"
                value={numericScore}
                onChange={(e) => setNumericScore(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg p-2 font-medium text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Feedback Text */}
          <div>
            <label className="block font-bold text-slate-700 mb-1 flex items-center gap-1">
              <FileText className="w-3.5 h-3.5 text-slate-500" /> Written Feedback & Comments
            </label>
            <textarea
              rows={4}
              placeholder="Enter detailed feedback or comments for the student..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              className="w-full bg-white border border-slate-300 rounded-xl p-3 font-normal text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none leading-relaxed resize-none"
            />
          </div>

          {/* E-Signature toggle */}
          {professorProfile?.signature && (
            <div className="flex items-center justify-between p-3 rounded-xl bg-indigo-50/60 border border-indigo-100">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeSig"
                  checked={includeSignature}
                  onChange={(e) => setIncludeSignature(e.target.checked)}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <label htmlFor="includeSig" className="font-semibold text-indigo-900 cursor-pointer">
                  Attach Saved Electronic Signature
                </label>
              </div>
              <img
                src={professorProfile.signature}
                alt="Signature preview"
                className="h-6 object-contain"
              />
            </div>
          )}

          {/* Footer Actions */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-slate-300 font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-md transition-all disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              <span>{submitting ? "Submitting..." : "Submit Review"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GradeActivityModal;
